/**
 * `/account` — customer-facing account settings.
 *
 * Customer-facing read-only profile.
 *
 * Sections rendered:
 *   - Profile (read-only — derived from /api/portal/me, no edit yet)
 *
 * Data source:
 *   - `GET   /api/portal/me`         — initial values (locale + profile)
 *
 * The page uses the Phase 1 UI primitives (`Card`, `Button`, `Spinner`,
 * `ErrorState`) and is wrapped by `AppShell` from `App.tsx`. No Tailwind
 * dependency — inline styles match the rest of customer-portal/web.
 *
 * Spec:
 *   - docs/specs/PORTAL_MIGRATION_FROM_PSM_SSOT.md §4.4 stream H
 *   - docs/specs/PORTAL_INTERNAL_API_CONTRACT.md §2.2 M1
 *   - packages/shared/src/portal/account.ts (zod enum + request schema)
 */
import { useEffect, useState } from 'react';
import { portalApi, PortalApiError } from '../lib/apiClient';
import { Card, CardBody, ErrorState, Spinner } from '../components/ui';

interface MeResponse {
  ok: boolean;
  data: {
    portal_user_id: number;
    email: string;
    status: string;
    scopes: { customer_id: number }[];
    customer: { id: number; name: string; locale: string | null } | null;
    counts: { vehicles: number; shipments: number; invoices_outstanding: number } | null;
    name: string | null;
    profile?: {
      email: string | null; company: string | null; member_type: 'individual' | 'corporate' | null; first_name: string | null; middle_name: string | null; last_name: string | null;
      phone: string | null; mobile: string | null; street_address: string | null; city: string | null; post_code: string | null; country: string | null;
      job_title: string | null; website: string | null; vehicle_interest: string | null; has_import_experience: boolean | null; additional_info: string | null;
      is_dealer: boolean | null; terms_agreed: boolean | null; marketing_agreed: boolean | null;
    };
  };
}
type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; me: MeResponse['data'] }
  | { kind: 'error'; message: string };


export function AccountSettingsPage(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ kind: 'loading' });
    portalApi<MeResponse>('/api/portal/me', { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        setState({ kind: 'ready', me: res.data });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof PortalApiError) {
          setState({ kind: 'error', message: err.code });
        } else {
          setState({ kind: 'error', message: 'UNKNOWN' });
        }
      });
    return () => ctrl.abort();
  }, [reloadTick]);

  const profile = state.kind === 'ready' ? state.me.profile : undefined;
  const profileRows: Array<[string, string]> = [
    ['Email', profile?.email ?? (state.kind === 'ready' ? state.me.email : '—')],
    ['Name', state.kind === 'ready' ? state.me.name ?? '—' : '—'],
    ['Member type', profile?.member_type === 'corporate' ? 'Corporate' : profile?.member_type === 'individual' ? 'Individual' : '—'],
    ['Company', profile?.company ?? '—'], ['First name', profile?.first_name ?? '—'], ['Middle name', profile?.middle_name ?? '—'], ['Last name', profile?.last_name ?? '—'],
    ['Telephone', profile?.phone ?? '—'], ['Mobile', profile?.mobile ?? '—'], ['Street address', profile?.street_address ?? '—'], ['City', profile?.city ?? '—'], ['Post code', profile?.post_code ?? '—'], ['Country', profile?.country ?? '—'],
    ['Job title', profile?.job_title ?? '—'], ['Website', profile?.website ?? '—'], ['Vehicle interest', profile?.vehicle_interest ?? '—'],
    ['Import experience', profile?.has_import_experience == null ? '—' : profile.has_import_experience ? 'Yes' : 'No'], ['Additional information', profile?.additional_info ?? '—'],
    ['Dealer authorization', profile?.is_dealer == null ? '—' : profile.is_dealer ? 'Yes' : 'No'], ['Terms agreement', profile?.terms_agreed == null ? '—' : profile.terms_agreed ? 'Yes' : 'No'], ['Marketing consent', profile?.marketing_agreed == null ? '—' : profile.marketing_agreed ? 'Yes' : 'No'],
    ['Customer', state.kind === 'ready' ? state.me.customer?.name ?? '—' : '—'],
  ];

  return (
    <section data-testid="account-settings-page" style={{ display: 'grid', gap: '1.5rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Account settings</h1>
        <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
          Profile
        </p>
      </header>

      {state.kind === 'loading' ? <Spinner block label="Loading account settings" /> : null}

      {state.kind === 'error' ? (
        <ErrorState
          title="We couldn't load your account information."
          description={`Error code: ${state.message}`}
          onRetry={() => setReloadTick((n) => n + 1)}
          retryLabel="Try again"
        />
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {/* Profile — read-only */}
          <Card aria-label="Profile">
            <CardBody>
              <h2
                style={{
                  margin: 0,
                  marginBottom: '0.75rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#111827',
                }}
              >
                Profile
              </h2>
              <dl
                data-testid="account-profile"
                style={{
                  margin: 0,
                  display: 'grid',
                  gridTemplateColumns: 'minmax(120px, max-content) 1fr',
                  rowGap: '0.5rem',
                  columnGap: '1rem',
                  fontSize: '0.875rem',
                }}
              >
                {profileRows.map(([label, value]) => (
                  <span key={label} style={{ display: 'contents' }}><dt style={{ color: '#6b7280' }}>{label}</dt><dd style={{ margin: 0, color: '#111827', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{value}</dd></span>
                ))}
              </dl>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                To edit your profile, please contact your representative.
              </p>
            </CardBody>
          </Card>

        </>
      ) : null}
    </section>
  );
}
